////////////////////////////////////////////////////////////////////////////////
//                                                                            //
//  IRONSIDE - Public Website GUI                                             //
//                                                                            //
//  Version: 1.0.6                                                            //
//  Summary: News article route — GET /public/news/:slug                    //
//                                                                            //
////////////////////////////////////////////////////////////////////////////////

import { useParams, Link } from 'react-router';
import { AxiosError } from 'axios';

import { usePageTitle } from '@/hooks/usePageTitle';
import { useApi } from '@/hooks/useApi';
import { newsArticleJsonLd, useJsonLd } from '@/lib/seo';
import { formatDate } from '@/lib/format';
import type { NewsDetailRecord } from '@/types/public';
import { Spinner } from '@/components/feedback/Spinner';
import { ErrorState } from '@/components/feedback/ErrorState';
import { Markdown } from '@/components/content/Markdown';
import { GlassTile } from '@/components/content/GlassTile';
import { PageHero } from '@/components/content/PageHero';
import { Reveal } from '@/components/content/Reveal';

export function NewsDetail () {
  const { slug } = useParams<{ slug: string }>();
  usePageTitle(slug ?? 'News article');

  const { status, data, error } = useApi<NewsDetailRecord>(slug ? `/public/news/${slug}` : null);

  // Before any early return — hooks must run unconditionally. Null until the
  // article loads; the script mounts only for a successfully rendered article.
  useJsonLd(status === 'success' ? newsArticleJsonLd(data) : null);

  if (status === 'loading') {
    return (
      <div>
        <PageHero title="News" />
        <div className="mx-auto max-w-3xl px-6 py-16"><Spinner label="Loading article" /></div>
      </div>
    );
  }

  if (status === 'error') {
    const notFound = error.cause instanceof AxiosError && error.cause.response?.status === 404;
    return (
      <div>
        <PageHero title="News" />
        <div className="mx-auto max-w-3xl px-6 py-16">
          <Reveal as="div">
            {notFound ? (
              <GlassTile role="alert" className="p-8 sm:p-12">
                <h2 className="text-3xl font-semibold text-ink">Article not found</h2>
                <p className="mt-4 text-muted">This article doesn't exist, or is no longer published.</p>
                <Link to="/news" className="mt-4 inline-block underline">Back to News</Link>
              </GlassTile>
            ) : (
              <ErrorState message="This article couldn't be loaded right now." />
            )}
          </Reveal>
        </div>
      </div>
    );
  }

  const article = data;

  return (
    <div>
      {/* The article's own title is the page's hero — a shorter (~60vh)
          maskRevealWord title over the persistent /news BackgroundLayer,
          consistent with every other public page (1.0.3.md §4), rather than a
          generic "News" title followed by a second, plain <h1>. */}
      <PageHero
        title={article.title}
        subtitle={`${formatDate(article.publishedAt)}${article.author ? ` · ${article.author}` : ''}`}
      />

      <Reveal as="article" className="mx-auto max-w-3xl px-6 py-16">
        {/* Sustained article-length reading copy — the quieter "prose" tile
            finish (1.0.3.md §6, styles/glass.css's `.glass-tile--prose`): the
            default heavier-transparency <GlassTile /> would fight long-form
            text readability. */}
        <GlassTile className="glass-tile--prose p-8 sm:p-12">
          <Markdown
            source={article.bodyMd}
            getImageAlt={() => `${article.title} — supporting image`}
          />
        </GlassTile>
      </Reveal>
    </div>
  );
}
